import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '../../store/uiSlice';
import { updateUser } from '../../store/authSlice';
import api from '../../services/api';

const QUESTIONS = [
  { page: '1/6', question: 'Main sources of funds available for trading', type: 'multiple_choice',
    options: ['Employment / Business Income','Savings and Investments','Retirement Income','Company Profit/Dividends','Inheritance','Property Sale','Loan/ Borrowings/Gifts','Other'] },
  { page: '2/6', question: 'What is the amount that you expect to deposit in the next 12 months? (USD)', type: 'multiple_choice',
    options: ['0 - 20,000','20,001 - 50,000','50,001 - 250,000','250,001 - 500,000','500,001 - 1,000,000','More than 1,000,000'] },
  { page: '3/6', question: 'Your total Annual Estimated Income (USD)', type: 'multiple_choice',
    options: ['Less than 50,000','50,001 - 100,000','100,001 - 500,000','500,001 - 1,000,000','More than 1,000,000'] },
  { page: '4/6', question: 'Are you a Politically Exposed Person ("PEP") or a Family Member of a Close Associate of a PEP?', type: 'yes_no', options: ['No', 'Yes'] },
  { page: '5/6', question: 'What is the nature of your profession/business or relevant former profession/business?', type: 'multiple_choice',
    options: ['Accounting and Finance Services','Company Service Providers','Real Estate','Pharmaceuticals and Healthcare','Arms Trade and Defence','Casinos','Banking','Financial Services','Computer Software, Internet, IT','Government Activities','Mathematics','Education','Engineering and/or Construction','Other'] },
  { page: '6/6', question: 'Do you understand that trading on CFDs carries significant risk of loss?', type: 'confirmation',
    options: ['Yes, I confirm I understand that trading in CFDs carries significant risk of loss.'] },
];

const QuestionnaireModal = ({ isOpen, onClose, onComplete }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (user?.questionnaire) {
      try {
        const existing = typeof user.questionnaire === 'string' ? JSON.parse(user.questionnaire) : user.questionnaire;
        setAnswers(existing);
        const allAnswered = QUESTIONS.every(q => existing[q.page] !== undefined && existing[q.page] !== null && existing[q.page] !== '');
        setIsCompleted(allAnswered);
      } catch (e) { setAnswers({}); }
    }
  }, [user]);

  if (!isOpen) return null;

  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  const isLastStep = currentStep === QUESTIONS.length - 1;
  const selectedAnswer = answers[currentQuestion.page] || '';

  const handleSelectOption = (option) => { setAnswers(prev => ({ ...prev, [currentQuestion.page]: option })); };
  const handleNext = () => {
    if (!selectedAnswer) { dispatch(showToast({ message: 'Please select an answer before continuing', type: 'error' })); return; }
    if (isLastStep) { handleSubmit(); } else { setCurrentStep(prev => Math.min(prev + 1, QUESTIONS.length - 1)); }
  };
  const handlePrev = () => { setCurrentStep(prev => Math.max(prev - 1, 0)); };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await api.post('/user/questionnaire', { answers });
      if (response.data.success) {
        dispatch(updateUser({ questionnaire: answers, questionnaireComplete: response.data.questionnaireComplete }));
        setIsCompleted(true);
        dispatch(showToast({ message: 'Questionnaire completed successfully!', type: 'success' }));
        if (onComplete) onComplete();
        setTimeout(() => onClose(), 1500);
      }
    } catch (error) {
      dispatch(showToast({ message: error.response?.data?.message || 'Failed to save questionnaire', type: 'error' }));
    } finally { setIsSubmitting(false); }
  };

  const handleSkip = () => {
    if (Object.keys(answers).length > 0) { api.post('/user/questionnaire', { answers }).catch(() => {}); dispatch(updateUser({ questionnaire: answers })); }
    onClose();
  };

  const getOptionStyle = (option) => {
    const isSelected = selectedAnswer === option;
    if (currentQuestion.type === 'confirmation') {
      return isSelected
        ? { background: 'var(--color-greenBg)', border: '2px solid var(--color-green)', color: 'var(--color-text)', boxShadow: '0 0 20px rgba(5,150,105,0.15)' }
        : { background: 'var(--color-bgSurfaceLight)', border: '2px solid var(--color-borderStrong)', color: 'var(--color-textSecondary)' };
    }
    return isSelected
      ? { background: 'var(--color-orangeBg)', border: '2px solid var(--color-orange)', color: 'var(--color-text)', boxShadow: '0 0 20px var(--color-orangeGlow)' }
      : { background: 'var(--color-bgSurfaceLight)', border: '2px solid var(--color-borderStrong)', color: 'var(--color-textSecondary)' };
  };

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 backdrop-blur-md" style={{ background: 'var(--color-bgOverlay)' }} onClick={handleSkip} />
      <div className="relative sm:rounded-2xl w-full h-full sm:h-auto sm:max-h-[95vh] max-w-[600px] shadow-2xl animate-pop-in overflow-hidden flex flex-col"
        style={{ background: 'var(--color-bg)', border: '2px solid var(--color-orangeBorder)' }}>
        
        {/* Header */}
        <div className="px-6 py-5 flex items-center justify-between flex-shrink-0"
          style={{ borderBottom: '2px solid var(--color-borderMedium)', background: 'var(--color-bgSurface)' }}>
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-lg"
              style={{ background: 'var(--color-orangeBg)', border: '2px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}>
              <i className="fas fa-clipboard-list" />
            </div>
            <div>
              <div className="font-syne text-lg font-extrabold" style={{ color: 'var(--color-text)' }}>Trading Questionnaire</div>
              <div className="text-xs font-mono font-bold" style={{ color: 'var(--color-textSecondary)' }}>
                {isCompleted ? '✓ Completed' : `Question ${currentStep + 1} of ${QUESTIONS.length}`}
              </div>
            </div>
          </div>
          <button onClick={handleSkip} className="w-9 h-9 rounded-lg cursor-pointer text-base flex items-center justify-center transition-all"
            style={{ background: 'var(--color-bgCard)', border: '2px solid var(--color-borderStrong)', color: 'var(--color-textSecondary)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-red)'; e.currentTarget.style.color = 'var(--color-red)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-borderStrong)'; e.currentTarget.style.color = 'var(--color-textSecondary)'; }}>
            <i className="fas fa-xmark" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-5 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>Progress</span>
            <span className="text-[11px] font-mono font-extrabold" style={{ color: 'var(--color-text)' }}>{Math.round(progress)}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-borderMedium)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, var(--color-orange), var(--color-purple))' }} />
          </div>
          <div className="flex justify-between mt-3 px-2">
            {QUESTIONS.map((q, idx) => (
              <div key={q.page} className="w-3 h-3 rounded-full transition-all duration-300"
                style={{ background: idx < currentStep ? 'var(--color-green)' : idx === currentStep ? 'var(--color-orange)' : 'var(--color-borderStrong)' }} />
            ))}
          </div>
        </div>

        {/* Question */}
        <div className="px-6 py-5 flex-shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] px-3 py-1 rounded-full font-mono font-extrabold border-2"
              style={{ background: 'var(--color-orangeBg)', color: 'var(--color-text)', borderColor: 'var(--color-orangeBorder)' }}>{currentQuestion.page}</span>
            <span className="text-[10px] font-mono font-bold uppercase tracking-wider" style={{ color: 'var(--color-textSecondary)' }}>
              {currentQuestion.type === 'confirmation' ? 'Confirmation' : currentQuestion.type === 'yes_no' ? 'Yes / No' : 'Select one option'}
            </span>
          </div>
          <h3 className="font-syne text-xl font-extrabold leading-snug mt-3" style={{ color: 'var(--color-text)' }}>{currentQuestion.question}</h3>
        </div>

        {/* Options */}
        <div className="px-6 pb-4 space-y-3 overflow-y-auto custom-scrollbar flex-1">
          {currentQuestion.options.map((option, idx) => (
            <button key={idx} onClick={() => handleSelectOption(option)}
              className="w-full text-left px-5 py-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-4"
              style={getOptionStyle(option)}>
              <div className="w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all"
                style={{ borderColor: selectedAnswer === option ? 'var(--color-orange)' : 'var(--color-borderStrong)', background: selectedAnswer === option ? 'var(--color-orange)' : 'transparent' }}>
                {selectedAnswer === option && <i className="fas fa-check text-[10px]" style={{ color: 'var(--color-textInverse)' }} />}
              </div>
              <span className="text-sm font-syne font-bold leading-tight">{option}</span>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ borderTop: '2px solid var(--color-borderMedium)', background: 'var(--color-bgSurface)' }}>
          <button onClick={handlePrev} disabled={currentStep === 0}
            className="px-5 py-3 rounded-xl text-xs font-syne font-bold transition-all disabled:opacity-20 disabled:cursor-not-allowed"
            style={{ color: 'var(--color-textSecondary)' }}>
            <i className="fas fa-arrow-left mr-2" /> Back
          </button>
          <div className="flex items-center gap-3">
            {!isCompleted && (
              <button onClick={handleSkip} className="px-5 py-3 rounded-xl text-xs font-syne font-bold transition-all"
                style={{ color: 'var(--color-textSecondary)' }}>Skip for now</button>
            )}
            <button onClick={handleNext} disabled={isSubmitting || !selectedAnswer}
              className="px-8 py-3 rounded-xl font-syne font-bold text-sm transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
              style={{ background: isLastStep ? 'var(--color-gradientGreen)' : 'var(--color-gradientOrange)', color: '#ffffff' }}>
              {isSubmitting ? <><span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                : isLastStep ? <><i className="fas fa-check text-xs" /> Submit</>
                : <>Next <i className="fas fa-arrow-right text-xs" /></>}
            </button>
          </div>
        </div>

        {/* Completed overlay */}
        {isCompleted && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 animate-fade-in" style={{ background: 'var(--color-bg)' }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-5"
              style={{ background: 'var(--color-greenBg)', border: '3px solid var(--color-green)' }}>
              <i className="fas fa-check-circle text-3xl" style={{ color: 'var(--color-green)' }} />
            </div>
            <h3 className="font-syne text-2xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>All Done!</h3>
            <p className="text-sm font-mono font-bold text-center px-8" style={{ color: 'var(--color-textSecondary)' }}>Your questionnaire has been completed. You can now place trades.</p>
            <button onClick={onClose} className="mt-8 px-10 py-3 rounded-xl font-syne font-bold text-base transition-all"
              style={{ background: 'var(--color-gradientGreen)', color: '#ffffff' }}>Start Trading</button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes pop-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-pop-in { animation: pop-in 0.25s ease-out; }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--color-scrollbarThumb); border-radius: 10px; }
      `}</style>
    </div>
  );
};

export default QuestionnaireModal;